import React, { useState, useEffect } from 'react';
import {
  BoltIcon,
  ChartBarIcon,
  ClockIcon,
  CpuChipIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowRightIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  EyeIcon,
  DocumentChartBarIcon,
  SignalIcon,
  XMarkIcon,
  BuildingOffice2Icon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import LoadingScreen from '../components/common/LoadingScreen';
import AnimatedCounter from '../components/common/AnimatedCounter';
import PulsingIndicator from '../components/common/PulsingIndicator';
import CommonPageHeader from '../components/common/CommonPageHeader';
import { energiaService, EnergiaPOD, ProviderSummary, SystemStatus, ProcessingLog } from '../services/energiaService';
import { useLanguage } from '../contexts/LanguageContext';
import { format, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns';
import { hu } from 'date-fns/locale';


interface DashboardStats {
  totalCustomers: number;
  totalConsumption: number;
  totalCost: number;
  activeProviders: number;
  weeklyConsumption: number;
  weeklyGrowth: number;
}

interface ConsumptionTrend {
  date: string;
  consumption: number;
  cost: number;
  customers: number;
}

const EnergiaDashboard = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [selectedPod, setSelectedPod] = useState<EnergiaPOD | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  
  // Dashboard section toggles
  const [showSystemMonitoring, setShowSystemMonitoring] = useState(false);
  const [showTrendAnalytics, setShowTrendAnalytics] = useState(false);
  const [showTopCustomers, setShowTopCustomers] = useState(false);

  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    totalCustomers: 0,
    totalConsumption: 0,
    totalCost: 0,
    activeProviders: 0,
    weeklyConsumption: 0,
    weeklyGrowth: 0
  });
  const [providerSummaries, setProviderSummaries] = useState<ProviderSummary[]>([]);
  const [systemStatuses, setSystemStatuses] = useState<SystemStatus[]>([]);
  const [recentLogs, setRecentLogs] = useState<ProcessingLog[]>([]);
  const [topCustomers, setTopCustomers] = useState<EnergiaPOD[]>([]);
  const [consumptionTrends, setConsumptionTrends] = useState<ConsumptionTrend[]>([]);

  useEffect(() => {
    loadDashboardData();
    // Refresh every 5 minutes
    const interval = setInterval(loadDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load all data in parallel
      const [customers, providers, statuses, logs] = await Promise.all([
        energiaService.getCustomers(),
        energiaService.getProviderSummaries(),
        energiaService.getSystemStatus(),
        energiaService.getProcessingLogs(undefined, 10)
      ]);

      // Calculate dashboard statistics
      const stats = calculateDashboardStats(customers || [], providers || []);
      setDashboardStats(stats);
      
      // Set provider summaries
      setProviderSummaries(providers || []);
      
      // Set system statuses
      setSystemStatuses(statuses || []);
      
      // Set recent logs
      setRecentLogs(logs || []);
      
      // Get top customers by consumption
      const sortedCustomers = (customers || [])
        .sort((a, b) => (b?.total_consumption_kwh || 0) - (a?.total_consumption_kwh || 0))
        .slice(0, 10);
      setTopCustomers(sortedCustomers);
      
      // Generate consumption trends (mock data for demonstration)
      const trends = generateConsumptionTrends(customers || []);
      setConsumptionTrends(trends);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateDashboardStats = (customers: EnergiaPOD[], providers: ProviderSummary[]): DashboardStats => {
    const safeCustomers = customers || [];
    const totalCustomers = safeCustomers.length;
    const totalConsumption = safeCustomers.reduce((sum, c) => sum + (c?.total_consumption_kwh || 0), 0);
    const totalCost = safeCustomers.reduce((sum, c) => sum + (c?.total_cost_huf || 0), 0);
    const activeProviders = new Set(safeCustomers.map(c => c?.provider).filter(Boolean)).size;
    
    // Calculate weekly consumption (mock calculation)
    const now = new Date();
    const weekStart = startOfWeek(now, { locale: hu });
    const weekEnd = endOfWeek(now, { locale: hu });
    
    const weeklyCustomers = safeCustomers.filter(c => {
      if (!c?.last_update?.toDate) return false;
      return isWithinInterval(c.last_update.toDate(), { start: weekStart, end: weekEnd });
    });
    
    const weeklyConsumption = weeklyCustomers.reduce((sum, c) => sum + (c?.total_consumption_kwh || 0), 0);
    const weeklyGrowth = Math.random() * 20 - 10; // Mock growth percentage
    
    return {
      totalCustomers,
      totalConsumption,
      totalCost,
      activeProviders,
      weeklyConsumption,
      weeklyGrowth
    };
  };

  const generateConsumptionTrends = (customers: EnergiaPOD[]): ConsumptionTrend[] => {
    // Generate last 7 days of trend data (mock)
    const trends: ConsumptionTrend[] = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      
      trends.push({
        date: format(date, 'MM.dd', { locale: hu }),
        consumption: Math.random() * 1000 + 500,
        cost: Math.random() * 50000 + 25000,
        customers: Math.floor(Math.random() * 10) + customers.length - 5
      });
    }
    return trends;
  };



  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'processing':
        return <ArrowPathIcon className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'error':
      case 'critical_error':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getProviderColor = (provider: string) => {
    const colors: Record<string, string> = {
      'EON': 'bg-orange-500',
      'MVMI': 'bg-indigo-500',
      'OPUS': 'bg-red-500',
      'EMASZ': 'bg-purple-500',
      'ELMU': 'bg-green-500',
      // Legacy support
      'demasz': 'bg-blue-500',
      'elmu': 'bg-green-500',
      'emasz': 'bg-purple-500',
      'eon': 'bg-orange-500',
      'eon-mvm': 'bg-amber-500',
      'opus': 'bg-red-500',
      'mvmi': 'bg-indigo-500'
    };
    return colors[provider] || 'bg-gray-500';
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Header */}
      <CommonPageHeader
        title="Energia Dashboard"
        subtitle="Valós idejű energia monitoring"
        icon={ChartBarIcon}
        onRefresh={loadDashboardData}
      />

      {/* Enhanced Stats Cards with clearer POD vs Customer metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 xl:gap-6">
        <div className="bg-white rounded-xl p-4 xl:p-6 shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Összes POD</p>
              <p className="text-gray-800 text-2xl font-bold mt-1">
                <AnimatedCounter 
                  value={dashboardStats.totalCustomers} 
                  formatter={(val) => val.toLocaleString('hu-HU')}
                  duration={1000}
                />
              </p>
              <p className="text-xs text-gray-500 mt-1">
                +{dashboardStats.weeklyGrowth.toFixed(1)}% (7 nap)
              </p>
            </div>
            <div className="p-3 rounded-lg bg-blue-100">
              <BoltIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 xl:p-6 shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Egyedi ügyfelek</p>
              <p className="text-gray-800 text-2xl font-bold mt-1">
                <AnimatedCounter 
                  value={Array.from(new Set(topCustomers.map(pod => pod.customer_info?.customer_name || pod.pod_id.split('-')[0]))).length} 
                  formatter={(val) => val.toLocaleString('hu-HU')}
                  duration={1000}
                />
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Átlag {topCustomers.length > 0 ? (dashboardStats.totalCustomers / Array.from(new Set(topCustomers.map(pod => pod.customer_info?.customer_name || pod.pod_id.split('-')[0]))).length).toFixed(1) : '0'} POD/ügyfél
              </p>
            </div>
            <div className="p-3 rounded-lg bg-green-100">
              <BuildingOffice2Icon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 xl:p-6 shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Összes fogyasztás</p>
              <p className="text-gray-800 text-2xl font-bold mt-1">
                <AnimatedCounter 
                  value={dashboardStats.totalConsumption} 
                  formatter={(val) => energiaService.formatKWH(val)}
                  duration={1000}
                />
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {energiaService.formatKWH(dashboardStats.weeklyConsumption)} (7 nap)
              </p>
            </div>
            <div className="p-3 rounded-lg bg-yellow-100">
              <ArrowTrendingUpIcon className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 xl:p-6 shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Összes költség</p>
              <p className="text-gray-800 text-2xl font-bold mt-1">
                <AnimatedCounter 
                  value={dashboardStats.totalCost} 
                  formatter={(val) => energiaService.formatHUF(val)}
                  duration={1000}
                />
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {dashboardStats.totalConsumption > 0 ? (dashboardStats.totalCost / dashboardStats.totalConsumption).toFixed(1) : '0'} Ft/kWh átlag
              </p>
            </div>
            <div className="p-3 rounded-lg bg-purple-100">
              <CurrencyDollarIcon className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 xl:p-6 shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Aktív szolgáltatók</p>
              <p className="text-gray-800 text-2xl font-bold mt-1">
                <AnimatedCounter 
                  value={dashboardStats.activeProviders} 
                  duration={1000}
                />
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {providerSummaries.length} összesen regisztrálva
              </p>
            </div>
            <div className="p-3 rounded-lg bg-indigo-100">
              <CpuChipIcon className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 xl:p-6 shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">CRM lefedettség</p>
              <p className="text-gray-800 text-2xl font-bold mt-1">
                <AnimatedCounter 
                  value={dashboardStats.totalCustomers > 0 ? Math.round(((topCustomers?.filter(pod => pod?.customer_info).length || 0) / dashboardStats.totalCustomers) * 100) : 0} 
                  suffix="%"
                  duration={1000}
                />
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {topCustomers?.filter(pod => pod?.customer_info).length || 0} POD CRM-ben
              </p>
            </div>
            <div className="p-3 rounded-lg bg-teal-100">
              <CheckCircleIcon className="w-6 h-6 text-teal-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Compact Real-time Monitoring Section */}
      <div className="bg-white rounded-xl p-6 shadow-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Rendszer monitoring</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowSystemMonitoring(!showSystemMonitoring)}
              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors ${
                showSystemMonitoring ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <SignalIcon className="w-4 h-4 mr-1" />
              Állapot
            </button>
            <button
              onClick={() => setShowTrendAnalytics(!showTrendAnalytics)}
              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors ${
                showTrendAnalytics ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <DocumentChartBarIcon className="w-4 h-4 mr-1" />
              Trendek
            </button>
            <button
              onClick={() => setShowTopCustomers(!showTopCustomers)}
              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors ${
                showTopCustomers ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <EyeIcon className="w-4 h-4 mr-1" />
              Top ügyfelek
            </button>
          </div>
        </div>

                 {/* Quick Summary when collapsed */}
         {!showSystemMonitoring && !showTrendAnalytics && !showTopCustomers && (
           <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
             <div className="text-center">
               <div className="flex items-center justify-center space-x-2 mb-1">
                 <PulsingIndicator 
                   status={Math.round((systemStatuses.filter(s => s.status === 'completed').length / Math.max(systemStatuses.length, 1)) * 100) > 80 ? 'success' : 'warning'}
                   size="sm"
                 />
                 <div className="text-lg font-bold text-green-600">
                   <AnimatedCounter 
                     value={Math.round((systemStatuses.filter(s => s.status === 'completed').length / Math.max(systemStatuses.length, 1)) * 100)}
                     suffix="%"
                     duration={1000}
                   />
                 </div>
               </div>
               <div className="text-xs text-gray-600">rendszer egészség</div>
             </div>
             <div className="text-center">
               <div className="flex items-center justify-center space-x-2 mb-1">
                 <PulsingIndicator 
                   status="active"
                   size="sm"
                 />
                 <div className="text-lg font-bold text-blue-600">
                   <AnimatedCounter 
                     value={dashboardStats.totalCustomers}
                     duration={1000}
                   />
                 </div>
               </div>
               <div className="text-xs text-gray-600">aktív POD</div>
             </div>
             <div className="text-center">
               <div className="text-lg font-bold text-purple-600">
                 {topCustomers.length > 0 ? Array.from(new Set(topCustomers.map(pod => pod.customer_info?.customer_name || pod.pod_id.split('-')[0]))).length : 0}
               </div>
               <div className="text-xs text-gray-600">egyedi ügyfél</div>
             </div>
           </div>
         )}

        {/* Expandable Monitoring Sections */}
        <div className="space-y-4">
          {/* System Health */}
          {showSystemMonitoring && (
            <div className="border rounded-lg p-4 bg-green-50/30">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-800">Rendszer állapot</h4>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-gray-600">ÉLŐBEN</span>
                  <button
                    onClick={() => setShowSystemMonitoring(false)}
                    className="text-gray-400 hover:text-gray-600 ml-2"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Rendszer egészség</span>
                    <span className="text-lg font-bold text-green-600">
                      {Math.round((systemStatuses.filter(s => s.status === 'completed').length / Math.max(systemStatuses.length, 1)) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-green-400 to-green-600 h-3 rounded-full transition-all duration-1000"
                      style={{ width: `${(systemStatuses.filter(s => s.status === 'completed').length / Math.max(systemStatuses.length, 1)) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {systemStatuses?.slice(0, 4).map((status) => (
                    <div key={status.provider} className="bg-white p-2 rounded-lg text-center">
                      <div className={`w-2 h-2 rounded-full mx-auto mb-1 ${
                        status.status === 'completed' ? 'bg-green-500' :
                        status.status === 'processing' ? 'bg-blue-500 animate-pulse' :
                        'bg-red-500'
                      }`}></div>
                      <div className="text-xs font-medium text-gray-800">
                        {status.provider.toUpperCase()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {status.monitoring?.health_score ? `${status.monitoring.health_score}%` : 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Trend Analytics */}
          {showTrendAnalytics && (
            <div className="border rounded-lg p-4 bg-blue-50/30">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-800">Fogyasztási trendek (7 nap)</h4>
                <button
                  onClick={() => setShowTrendAnalytics(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="text-xs font-medium text-gray-700 mb-2">Napi fogyasztás</h5>
                  <div className="space-y-1">
                    {consumptionTrends?.slice(0, 5).map((trend, index) => (
                      <div key={index} className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">{trend.date}</span>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{energiaService.formatKWH(trend.consumption)}</span>
                          <div className="w-16 bg-gray-200 rounded-full h-1.5">
                            <div 
                              className="bg-blue-500 h-1.5 rounded-full" 
                              style={{ width: `${(trend.consumption / 1500) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h5 className="text-xs font-medium text-gray-700 mb-2">Napi költség</h5>
                  <div className="space-y-1">
                    {consumptionTrends?.slice(0, 5).map((trend, index) => (
                      <div key={index} className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">{trend.date}</span>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{energiaService.formatHUF(trend.cost)}</span>
                          <div className="w-16 bg-gray-200 rounded-full h-1.5">
                            <div 
                              className="bg-green-500 h-1.5 rounded-full" 
                              style={{ width: `${(trend.cost / 75000) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="mt-3 pt-3 border-t border-blue-200 grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-sm font-bold text-blue-600">
                    {energiaService.formatKWH(consumptionTrends?.reduce((sum, t) => sum + t.consumption, 0) || 0)}
                  </div>
                  <div className="text-xs text-gray-500">7 napos</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-green-600">
                    {energiaService.formatHUF(consumptionTrends?.reduce((sum, t) => sum + t.cost, 0) || 0)}
                  </div>
                  <div className="text-xs text-gray-500">költség</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-purple-600">
                    {consumptionTrends && consumptionTrends.length > 0 ? 
                      ((consumptionTrends.reduce((sum, t) => sum + t.cost, 0) / consumptionTrends.reduce((sum, t) => sum + t.consumption, 0)) || 0).toFixed(1) : 
                      '0'} Ft/kWh
                  </div>
                  <div className="text-xs text-gray-500">átlagár</div>
                </div>
              </div>
            </div>
          )}

          {/* Top Customers */}
          {showTopCustomers && (
            <div className="border rounded-lg p-4 bg-purple-50/30">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-800">Top 5 ügyfél</h4>
                <button
                  onClick={() => setShowTopCustomers(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-2">
                {topCustomers?.slice(0, 5).map((customer, index) => {
                  const avgPrice = (customer?.total_consumption_kwh || 0) > 0 ? (customer?.total_cost_huf || 0) / (customer?.total_consumption_kwh || 1) : 0;
                  
                  return (
                    <div key={customer.id} className="bg-white p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                            index === 0 ? 'bg-yellow-100 text-yellow-800' :
                            index === 1 ? 'bg-gray-100 text-gray-800' :
                            index === 2 ? 'bg-orange-100 text-orange-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            #{index + 1}
                          </span>
                          <span className="text-sm font-medium text-gray-800 font-mono">
                            {customer?.pod_id?.slice(-8) || 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          {customer.customer_info ? (
                            <CheckCircleIcon className="w-3 h-3 text-green-500" />
                          ) : (
                            <ExclamationTriangleIcon className="w-3 h-3 text-orange-500" />
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center">
                          <div className="text-gray-500">kWh</div>
                          <div className="font-medium">{(customer.total_consumption_kwh / 1000).toFixed(0)}k</div>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-500">Ft</div>
                          <div className="font-medium">{(customer.total_cost_huf / 1000000).toFixed(1)}M</div>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-500">Ár</div>
                          <div className="font-medium">{avgPrice.toFixed(1)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>



      {/* Charts and Analytics Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Consumption Trends Chart */}
        <div className="bg-white rounded-xl p-6 shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Fogyasztási trendek (7 nap)</h3>
            <DocumentChartBarIcon className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            {consumptionTrends?.map((trend, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{trend.date}</span>
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium">
                    {energiaService.formatKWH(trend.consumption)}
                  </span>
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full" 
                      style={{ width: `${(trend.consumption / 1500) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Customers */}
        <div className="bg-white rounded-xl p-6 shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Top ügyfelek (fogyasztás)</h3>
            <EyeIcon className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            {topCustomers?.slice(0, 5).map((customer, index) => (
              <div key={customer.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    #{index + 1}
                  </span>
                  <span className="text-sm font-medium text-gray-800 font-mono">
                    {customer.pod_id.slice(-6)}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${getProviderColor(customer.provider).replace('bg-', 'bg-opacity-20 text-')}800`}>
                    {customer.provider.toUpperCase()}
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  {energiaService.formatKWH(customer.total_consumption_kwh)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>


      {/* Provider Status and System Health */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Provider Summaries */}
        <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-md">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Szolgáltatói összesítések</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-sm font-medium text-gray-600">Szolgáltató</th>
                  <th className="text-left py-2 text-sm font-medium text-gray-600">Ügyfelek</th>
                  <th className="text-left py-2 text-sm font-medium text-gray-600">Fogyasztás</th>
                  <th className="text-left py-2 text-sm font-medium text-gray-600">Érték</th>
                  <th className="text-left py-2 text-sm font-medium text-gray-600">Frissítve</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {providerSummaries?.map((provider) => (
                  <tr key={provider.provider} className="hover:bg-gray-50">
                    <td className="py-3">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${getProviderColor(provider.provider)}`}></div>
                        <span className="font-medium text-gray-800">{provider.provider.toUpperCase()}</span>
                      </div>
                    </td>
                    <td className="py-3 text-sm text-gray-600">
                      {provider.total_customers?.toLocaleString('hu-HU') || '0'}
                    </td>
                    <td className="py-3 text-sm text-gray-600">
                      {energiaService.formatKWH(provider.total_consumption_kwh)}
                    </td>
                    <td className="py-3 text-sm text-gray-600">
                      {energiaService.formatHUF(provider.total_cost_huf)}
                    </td>
                    <td className="py-3 text-sm text-gray-500">
                      {provider.upload_timestamp?.toDate ? 
                        format(provider.upload_timestamp.toDate(), 'MM.dd HH:mm', { locale: hu }) :
                        'N/A'
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* System Status */}
        <div className="bg-white rounded-xl p-6 shadow-md">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Rendszer állapot</h3>
          <div className="space-y-3">
            {systemStatuses?.map((status) => (
              <div key={status.provider} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(status.status)}
                  <span className="text-sm font-medium text-gray-800">
                    {status.provider.toUpperCase()}
                  </span>
                </div>
                <div className="text-right">
                  <div className={`text-xs px-2 py-1 rounded-full ${
                    status.status === 'completed' ? 'bg-green-100 text-green-800' :
                    status.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {status.status === 'completed' ? 'Aktív' :
                     status.status === 'processing' ? 'Feldolgozás' :
                     'Hiba'
                    }
                  </div>
                  {status.monitoring?.health_score && (
                    <div className="text-xs text-gray-500 mt-1">
                      {status.monitoring.health_score}% egészség
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Processing Logs */}
      <div className="bg-white rounded-xl p-6 shadow-md">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Legutóbbi feldolgozások</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 text-sm font-medium text-gray-600">Időpont</th>
                <th className="text-left py-2 text-sm font-medium text-gray-600">Szolgáltató</th>
                <th className="text-left py-2 text-sm font-medium text-gray-600">Állapot</th>
                <th className="text-left py-2 text-sm font-medium text-gray-600">Rekordok</th>
                <th className="text-left py-2 text-sm font-medium text-gray-600">Ügyfelek</th>
                <th className="text-left py-2 text-sm font-medium text-gray-600">Fogyasztás</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recentLogs?.map((log, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="py-3 text-sm text-gray-600">
                    {log.timestamp?.toDate ? 
                      format(log.timestamp.toDate(), 'MM.dd HH:mm', { locale: hu }) :
                      'N/A'
                    }
                  </td>
                  <td className="py-3">
                    <span className="text-sm font-medium text-gray-800">
                      {log.provider.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      log.status === 'completed' ? 'bg-green-100 text-green-800' :
                      log.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {log.status === 'completed' ? 'Sikeres' :
                       log.status === 'processing' ? 'Folyamatban' :
                       'Hiba'
                      }
                    </span>
                  </td>
                  <td className="py-3 text-sm text-gray-600">
                    {log.processing_details?.records_processed ? log.processing_details.records_processed.toLocaleString('hu-HU') : 'N/A'}
                  </td>
                  <td className="py-3 text-sm text-gray-600">
                    {log.processing_details?.customers_processed ? log.processing_details.customers_processed.toLocaleString('hu-HU') : 'N/A'}
                  </td>
                  <td className="py-3 text-sm text-gray-600">
                    {log.processing_details?.total_consumption_kwh ? 
                      energiaService.formatKWH(log.processing_details.total_consumption_kwh) :
                      'N/A'
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
        <h3 className="text-lg font-semibold mb-4">Gyors műveletek</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="/energia-pods"
            className="flex items-center justify-between p-4 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
          >
            <div>
              <div className="text-sm font-medium">POD Management</div>
              <div className="text-xs opacity-90">Ügyfél POD-ok kezelése</div>
            </div>
            <ArrowRightIcon className="w-5 h-5" />
          </a>
          
          <a
            href="/energia-billing"
            className="flex items-center justify-between p-4 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
          >
            <div>
              <div className="text-sm font-medium">Billing Queue</div>
              <div className="text-xs opacity-90">Számlázási sor kezelése</div>
            </div>
            <ArrowRightIcon className="w-5 h-5" />
          </a>
          
          <button
            onClick={loadDashboardData}
            className="flex items-center justify-between p-4 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
          >
            <div>
              <div className="text-sm font-medium">Refresh Data</div>
              <div className="text-xs opacity-90">Adatok frissítése</div>
            </div>
            <ArrowPathIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnergiaDashboard; 